import { apiClient } from '@/lib/api';

export interface SurveyData {
  ageBand?: 'child' | 'young-adult' | 'adult' | 'senior';
  biologicalSex?: 'male' | 'female';
  ethnicity?: string;
  symptomPresence?: 'yes' | 'no';
  symptomDuration?: '<24h' | '1-3days' | '4-7days' | '>7days';
  priorTestResult?: 'positive' | 'negative' | 'none';
  geographicRegion?: string;
}

export interface CreateTestRequest {
  profileId: string;
  sampleId?: string;
  sampleCode?: string;
  notes?: string;
  additionalInfo?: SurveyData;
  electrodeTypeId?: string;
  deviceState?: DeviceStateSnapshot;
  /** Overrides the profile's ImpedanceExecJson in the snapshot when params were tweaked for this run. */
  impedanceExecJsonOverride?: string;
}

export interface TestResponse {
  id: string;
  patientId?: string;
  sampleCode: string;
  electrodeTypeId?: string;
  status: string;
  createdAt: string;
  notes?: string;
  additionalInfo?: SurveyData;
  pathogenClassificationResults?: PathogenClassificationResult[];
}

export interface DeviceStateSnapshot {
  batteryLevel?: number;
  batteryVoltage?: number;
  temperature?: number;
  humidity?: number;
  powerState?: string;
  electrodeType?: string;
  isBusy?: boolean;
  progressPercent?: number;
  warnings?: number;
}

export interface BaselineMeasurementPayload {
  impedanceResultJson: any;
  completedAt: string;
  deviceUid: string;
  firmwareVersion: string;
  hardwareVersion: string;
  deviceState?: DeviceStateSnapshot;
}

export interface SpecimenMeasurementPayload {
  impedanceResultJson: any;
  startedAt: string;
  completedAt: string;
  deviceUid: string;
  firmwareVersion: string;
  deviceState?: DeviceStateSnapshot;
}

export interface ChannelClassificationResult {
  channelId: number;
  status: 'positive' | 'negative' | 'inconclusive';
  confidence?: number;
  concentration?: number;
  markerIds?: string[];
}

export interface PathogenClassificationResult {
  pathogenName: string;
  channelId: number;
  status: 'positive' | 'negative' | 'inconclusive' | null;
  confidence: number;
  concentration?: number;
}

export interface TestDetailResponse extends TestResponse {
  operatorName?: string;
  deviceUid?: string;
  completedAt?: string;
  /** Set by the backend when the baseline measurement is saved. */
  baselineCompletedAt?: string;
  /** Set by the backend when the specimen measurement is saved. */
  specimenCompletedAt?: string;
}

export const testService = {
  /**
   * Get test by ID
   * GET /api/v1/tests/{id}
   */
  async getTestById(testId: string): Promise<TestDetailResponse> {
    const response = await apiClient.get<TestDetailResponse>(`/api/v1/tests/${testId}`);
    return response.data;
  },

  /**
   * Create a new test
   * POST /api/v1/tests
   */
  async createTest(data: CreateTestRequest): Promise<TestResponse> {
    const response = await apiClient.post<TestResponse>('/api/v1/tests', data);
    return response.data;
  },

  /**
   * Start a test (transition from 'created' to 'running')
   * POST /api/v1/tests/{id}/start
   */
  async startTest(testId: string): Promise<TestResponse> {
    const response = await apiClient.post<TestResponse>(`/api/v1/tests/${testId}/start`);
    return response.data;
  },

  /**
   * Save baseline measurement data
   * POST /api/v1/tests/{id}/baseline
   */
  async saveBaseline(testId: string, payload: BaselineMeasurementPayload): Promise<void> {
    await apiClient.post(`/api/v1/tests/${testId}/baseline`, payload);
  },

  /**
   * Save specimen measurement data and trigger classification
   * POST /api/v1/tests/{id}/specimen
   */
  async saveSpecimen(testId: string, payload: SpecimenMeasurementPayload): Promise<void> {
    await apiClient.post(`/api/v1/tests/${testId}/specimen`, payload);
  },

  /**
   * Cancel a test
   * POST /api/v1/tests/{id}/cancel
   */
  async cancelTest(testId: string): Promise<TestResponse> {
    const response = await apiClient.post<TestResponse>(`/api/v1/tests/${testId}/cancel`);
    return response.data;
  },

  /**
   * Update test notes
   * PATCH /api/v1/tests/{id}/notes
   */
  async updateTestNotes(testId: string, notes: string): Promise<void> {
    await apiClient.patch(`/api/v1/tests/${testId}/notes`, { notes });
  },

  /**
   * Update test additional info (survey data)
   * PATCH /api/v1/tests/{id}/additional-info
   */
  async updateTestAdditionalInfo(testId: string, additionalInfo: SurveyData | null): Promise<void> {
    await apiClient.patch(`/api/v1/tests/${testId}/additional-info`, { additionalInfo });
  },

  /**
   * Update test patient association
   * PATCH /api/v1/tests/{id}/patient
   */
  async updateTestPatient(testId: string, patientId: string | null): Promise<void> {
    await apiClient.patch(`/api/v1/tests/${testId}/patient`, { patientId });
  },

  /**
   * Get impedance spectra for a single test
   * GET /api/v1/tests/{id}/spectra
   */
  async getTestSpectra(testId: string): Promise<TestSpectraResponse> {
    const response = await apiClient.get<TestSpectraResponse>(`/api/v1/tests/${testId}/spectra`);
    return response.data;
  },

  /**
   * Get impedance spectra for multiple tests in one call (max 100)
   * GET /api/v1/tests/batch-spectra?ids=...
   */
  async getBatchSpectra(testIds: string[]): Promise<TestSpectraResponse[]> {
    if (testIds.length === 0) return [];
    const ids = testIds.join(',');
    const response = await apiClient.get<TestSpectraResponse[]>(`/api/v1/tests/batch-spectra`, {
      params: { ids },
    });
    return response.data;
  },
};

export interface TestSpectraResponse {
  testId: string;
  measuredAt?: string;
  baselineResultJson: RawImpedanceResultJson;
  specimenResultJson: RawImpedanceResultJson;
  baselineCompletedAt: string;
  baselineStartedAt?: string;
  specimenStartedAt: string;
  profileSnapshotJson?: string;
  importSourceBaseline?: string;
  importSourceSpecimen?: string;
  notes?: string;
  deviceUid?: string;
}

export interface RawImpedanceResultJson {
  frequency_Hz: number[];
  values: { magnitude_Ohm: number; phase_rad: number }[];
}